import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { dramas } from '@/mocks/dramas';
import { newsList } from '@/mocks/news';
import DramaCard from '@/components/feature/DramaCard';
import NewsCard from '@/components/feature/NewsCard';
import Seo from '@/components/feature/Seo';

type ResultType = 'drama' | 'news';

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialWord = searchParams.get('q') ?? '';
  const [input, setInput] = useState(initialWord);
  const [keyword, setKeyword] = useState(initialWord);
  const [type, setType] = useState<ResultType>('drama');

  useEffect(() => {
    const word = searchParams.get('q') ?? '';
    setInput(word);
    setKeyword(word);
  }, [searchParams]);

  const normalized = keyword.trim().toLowerCase();

  const dramaResults = useMemo(() => {
    if (!normalized) return [];
    return dramas.filter((drama) => {
      const haystack = [drama.title, drama.synopsis, drama.genres.join(' '), drama.actors.join(' '), drama.director, drama.tags.join(' ')]
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalized);
    });
  }, [normalized]);

  const newsResults = useMemo(() => {
    if (!normalized) return [];
    return newsList.filter((article) => {
      const haystack = [article.title, article.summary, article.category, article.content.join(' ')].join(' ').toLowerCase();
      return haystack.includes(normalized);
    });
  }, [normalized]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    setKeyword(value);
    setSearchParams(value ? { q: value } : {});
  };

  const results = type === 'drama' ? dramaResults : newsResults;
  const hotDramas = dramas.slice(0, 4);
  const hotNews = newsList.slice(0, 3);

  return (
    <div className="w-full">
      <Seo
        title={keyword ? `搜索“${keyword}” - 上司的老婆影视剧内容站` : '站内搜索 - 上司的老婆影视剧内容站'}
        description="站内搜索电视剧与影视资讯，输入剧名、演员或关键词，快速找到对应的剧情介绍与相关资讯。"
        keywords="站内搜索,电视剧搜索,影视资讯搜索,剧情介绍"
      />

      <section className="bg-secondary-50 border-b border-secondary-100">
        <div className="px-4 md:px-6 lg:px-10 py-10 md:py-14">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-4">
            <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">站内搜索</span>
          </nav>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">站内搜索</h1>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-3xl">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入剧名、演员、题材或资讯关键词"
                className="w-full pl-11 pr-4 py-3.5 text-sm rounded-md bg-background-50 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <button
              type="submit"
              className="px-8 py-3.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索
            </button>
          </form>
          {keyword ? (
            <p className="text-sm text-foreground-600 mt-4">
              当前关键词：<span className="font-medium text-primary-600">“{keyword}”</span>
            </p>
          ) : null}
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 py-8 md:py-10">
        {keyword ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100">
                <button
                  type="button"
                  onClick={() => setType('drama')}
                  className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    type === 'drama' ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
                  }`}
                >
                  电视剧 {dramaResults.length}
                </button>
                <button
                  type="button"
                  onClick={() => setType('news')}
                  className={`px-4 py-2 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    type === 'news' ? 'bg-primary-500 text-background-50' : 'text-foreground-600'
                  }`}
                >
                  影视资讯 {newsResults.length}
                </button>
              </div>
              <span className="text-sm text-foreground-500">
                共找到 <span className="font-medium text-accent-600">{results.length}</span> 条结果
              </span>
            </div>

            {results.length > 0 ? (
              type === 'drama' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                  {dramaResults.map((drama) => (
                    <DramaCard key={drama.id} drama={drama} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {newsResults.map((article) => (
                    <NewsCard key={article.id} article={article} />
                  ))}
                </div>
              )
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl mb-4">
                  <i className="ri-search-eye-line"></i>
                </span>
                <h2 className="font-heading text-lg font-bold text-foreground-900">没有找到相关内容</h2>
                <p className="text-sm text-foreground-500 mt-2 max-w-md">
                  试试更换关键词，或者从下方热门推荐中找到你感兴趣的内容。
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-3xl mb-4">
              <i className="ri-search-2-line"></i>
            </span>
            <h2 className="font-heading text-lg font-bold text-foreground-900">输入关键词开始搜索</h2>
            <p className="text-sm text-foreground-500 mt-2 max-w-md">支持搜索剧名、演员、题材与资讯标题。</p>
          </div>
        )}

        {(!keyword || results.length === 0) ? (
          <div className="mt-12">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5">热门推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-10">
              {hotDramas.map((drama) => (
                <DramaCard key={drama.id} drama={drama} />
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {hotNews.map((article) => (
                <NewsCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
}